import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { ExtendedTheme } from "@insite/mobius/ThemeProvider";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    currentPageUrl?:string;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname.toLowerCase().startsWith("/brand/") ? "brand-page" : "";

    return {
        view: state.pages.productList.view,
        viewSearchPage: state.pages.productList.isSearchPage,
        currentPageUrl,
    }
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & OwnProps;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    customfilterStyle?: InjectableCss;
    customlistingStyle?: InjectableCss;
}

export const columnsStyles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 2],
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 3, 2],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 10],
    },
    rightColumnTableViewGridItem: {
        width: [12, 12, 12, 9, 10],
    },
    customfilterStyle: {
        css: css`
            background: #fff;
            @media screen and (min-width: 80em) {
                margin-top: -5rem !important;
                &.filter-wrapper .brandLogoWrapper {
                    display: none;
                }
            }
            @media screen and (max-width: 79.99em) {
                margin-top: 1.5625rem !important;
                &.filter-wrapper .brandLogoWrapper {
                    background: #F7F9FA;
                    box-shadow: 10rem 0 0 #F7F9FA, -10rem 0 0 #F7F9FA;
                    margin-left: -20px;
                    margin-right: -20px;
                }
            }
            
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            background: #fff;
                        `,
                        null,
                        null,
                        css`
                            flex-basis: 17rem;
                            max-width: 17rem;
                            padding-left: 1.5625rem;
                        `,
                        null,
                    ],
                    "min",
                )};
        `,
    },
    customlistingStyle: {
        css: css`
        @media screen and (max-width: 79.99em) {
            &.list-wrapper .brandLogoWrapper {
                display: none;
            }
        }
        @media screen and (min-width: 80em) { 
                    height:auto;
                    width:wuto;
                    flex-basis: 80% !important;
                    max-width: 80%;
                    padding: .625rem .625rem .625rem 1.7rem;
        }
        @media screen and (min-width: 1280px) and (max-width: 1399px) {
                    flex-basis: 78%;
                    max-width: 78%;
            }
        `,
    }
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({ id, view, viewSearchPage, currentPageUrl }) => {
    const leftColumnGridItemStyle = view === "Table" ? styles.leftColumnTableViewGridItem : styles.leftColumnGridItem;
    const rightColumnGridItemStyle =
        view === "Table" ? styles.rightColumnTableViewGridItem : styles.rightColumnGridItem;

    if (viewSearchPage) {
        styles.customfilterStyle = {
            css: css`
                    background: #fff;
                    h3:empty {
                        display: none;
                    }
                    @media screen and (min-width: 1400px) {
                        margin-top: -2.625rem !important;
                    }
                    @media screen and (min-width: 80em) {
                        &.filter-wrapper .brandLogoWrapper {
                            display: none;
                        }
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                background: #fff;
                            `,
                            null,
                            null,
                            css`
                                flex-basis: 17rem;
                                max-width: 17rem;
                                padding-left: 1.5625rem;
                            `,
                            null,
                        ],
                        "min",
                    )};
                `,
        }
    }
    return (
        <GridContainer {...styles.container}>
            <GridItem {...leftColumnGridItemStyle} {...styles.customfilterStyle} className={`filter-wrapper ${currentPageUrl}`}>
                <Zone contentId={id} zoneName="Content00" />
            </GridItem>
            <GridItem {...rightColumnGridItemStyle} {...styles.customlistingStyle} className="list-wrapper">
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: [ProductListPageContext],
    },
};

export default widgetModule;